<template>
  <main class="guided-mode-page">
    <DangerSignBar />
    <div v-if="!hasDefinitions">
      <assessment-mode-placeholder
          :description="String($t('app.assessment_mode.guided.description'))"
          :icon="mdiClipboardCheckOutline"
          :title="String($t('app.assessment_mode.selection.guided.title'))"
      />
    </div>
    <div v-else class="guided-mode__shell">
      <header class="guided-mode__header">
        <div>
          <p class="guided-mode__eyebrow">{{ $t('app.assessment_mode.guided.header') }}
            <span v-if="view !== 'demographics'"> · {{ trackLabel }}</span>
          </p>
          <h1 v-if="heading">{{ heading }}</h1>
        </div>
      </header>
      <GuidedModeDemographics
        v-if="view === 'demographics'"
        :age="age"
        :age-unit="ageUnit"
        :age-minimum="ageMinimum"
        :age-maximum="ageMaximum"
        :age-prompt="demographicAgePrompt"
        :age-title="demographicAgeTitle"
        :age-invalid="ageInvalid"
        :age-error="ageError"
        :sex="sex"
        :sex-prompt="demographicSexPrompt"
        :sex-options="demographicSexOptions"
        :sex-disabled="sexEditingDisabled"
        :identifier-label="identifierLabel"
        :identifier-value="identifierValue"
        :identifier-error="identifierError"
        :identifier-invalid="identifierInvalid"
        :maternal-status="maternalStatus"
        :maternal-status-disabled="maternalStatusEditingDisabled"
        :collect-maternal-status="collectMaternalStatus"
        :assessment-label="assessmentLabel"
        :assessment-unavailable="assessmentUnavailable"
        @age-changed="setAge"
        @age-unit-changed="setAgeUnit"
        @sex-changed="setSex"
        @identifier-changed="setIdentifier"
        @maternal-status-changed="setMaternalStatus"
      />
      <GuidedModeSymptoms
        v-else-if="view === 'symptoms'"
        :symptoms="symptoms"
        :selected-ids="selectedSymptomIds"
        :other-selected="otherSymptomSelected"
        :comprehensive-selected="comprehensiveSelected"
        @toggle-symptom="toggleSymptom"
        @other-changed="setOtherSymptom"
        @comprehensive-changed="setComprehensive"
      />
      <!-- Sequential group: display all questions on one scrollable screen -->
      <GuidedModeSequentialGroup
        v-else-if="view === 'question' && isSequentialGroup"
        :group-title="groupPrompt"
        :instructions="''"
        :question-items="sequentialGroupQuestionItems"
        :media-state="mediaState"
        :group-media="groupMedia"
        :group-media-state="groupMediaState"
        @answer="handleSequentialGroupAnswer"
        @option-selected="handleSequentialGroupOptionSelected"
        @blood-pressure-part="handleSequentialGroupBloodPressurePart"
        @blood-pressure-unknown="handleSequentialGroupUnknownAnswer"
        @date-changed="handleSequentialGroupDate"
        @toggle-size="handleSequentialGroupToggleSize"
        @toggle-group-size="toggleGroupMediaSize"
        @toggle-audio="handleSequentialGroupToggleAudio"
        @entry-complete="handleSequentialGroupEntryComplete"
      />
      <!-- Multiselect group: display as a panel with multiple selections -->
      <GuidedModeQuestionGroup
          v-else-if="view === 'question' && currentGroup && currentGroup.multiselect"
          :prompt="descPrompt || groupPrompt"
          :instructions="questionInstructions"
          :media="groupMedia"
          :media-size="groupMediaState.size"
          :options="groupOptions"
          :selected-values="selectedGroupOptions"
          @option-selected="selectGroupOption"
          @toggle-size="toggleGroupMediaSize"
      />

      <GuidedModeBloodPressure
        v-else-if="view === 'question' && questionCollectionType === 'blood_pressure'"
        :prompt="questionPrompt"
        :instructions="questionInstructions"
        :media="questionMedia"
        :systolic="bloodPressureSystolic"
        :diastolic="bloodPressureDiastolic"
        :systolic-invalid="bloodPressureSystolicInvalid"
        :diastolic-invalid="bloodPressureDiastolicInvalid"
        :systolic-warning="bloodPressureSystolicWarning"
        :diastolic-warning="bloodPressureDiastolicWarning"
        :ranges="bloodPressureRanges"
        :display-unit="displayUnit"
        :validation-severity="currentValidation ? currentValidation.severity : null"
        :validation-message-token="questionValidationMessageToken"
        :allow-unknown="questionAllowsUnknown"
        :unknown="currentAnswer === 'unknown'"
        @part-changed="setBloodPressurePart"
        @unknown="setUnknownAnswer"
      />
      <GuidedModeDate
          v-else-if="view === 'question' && questionCollectionType === 'calculated_date'"
          :prompt="questionTitle"
          :media="questionMedia"
          :value="String(currentAnswer || '').slice(0, 10)"
          calculated
      />
      <GuidedModeDate
          v-else-if="view === 'question' && questionCollectionType === 'date'"
          :prompt="questionPrompt"
          :media="questionMedia"
          :value="guidedDateValue"
          :min="guidedDateMin"
          :max="guidedDateMax"
          :invalid="guidedDateInvalid"
          @date-changed="setGuidedDate"
      />
      <GuidedModeQuestion
        v-else-if="view === 'question'"
        :prompt="questionPrompt"
        :instructions="questionInstructions"
        :media="questionMedia"
        :playback-rate="questionPlaybackRate"
        media-audio-control
        :audio-enabled="questionAudioEnabled"
        :options="questionOptions"
        :collection="questionCollection"
        :answer="currentInputAnswer"
        :selected-values="selectedOptions"
        :multiple="questionAllowsMultiple"
        :display-unit="displayUnit"
        :numeric-step="numericStep"
        :widget="questionWidget"
        :input-id="inputId"
        :validation-severity="currentValidation ? currentValidation.severity : null"
        :validation-message-token="questionValidationMessageToken"
        @answer="setAnswer"
        @option-selected="selectOption"
        @toggle-audio="toggleQuestionAudio"
      />
      <GuidedModeCompletionPrompt
        v-else-if="view === 'completion'"
        @complete="completeFocusedAssessment"
        @continue="continueAssessment"
      />
      <GuidedModeSummary v-else-if="view === 'summary'" :demographics="demographicItems" :items="answeredItems" :symptoms="symptomSummary" @edit="editQuestion" @edit-demographics="editDemographics" @edit-symptoms="editSymptoms" />
      <GuidedModeNavigation :next-disabled="!canContinue" :progress-segments="progressSegments" @previous="goPrevious" @next="handleNext" />
      <UnavailableAssessmentDialog
        v-if="showUnavailableAssessment"
        :message="unavailableAssessmentMessage"
        :patient-groups="availablePatientGroups"
        @close="showUnavailableAssessment = false"
      />
      <NetworkErrorState
        v-if="networkError"
        :message="$t('app.assessment_mode.guided.network_error.message')"
        @retry="retryAfterNetworkError"
        @back="dismissNetworkError"
      />
      <DangerSignAlertDialog v-if="dangerSignStore.hasPendingAlert" />
      <div v-if="showFollowUpWarning" class="guided-mode-follow-up-warning__backdrop">
        <section
          class="guided-mode-follow-up-warning"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="guided-mode-follow-up-warning-title"
          aria-describedby="guided-mode-follow-up-warning-description"
          tabindex="-1"
          @keydown.esc="cancelFollowUpChange"
        >
          <h2 id="guided-mode-follow-up-warning-title">{{ $t('app.dialog.attention_title') }}</h2>
          <p id="guided-mode-follow-up-warning-description">{{ $t('app.dialog.input_followup') }}</p>
          <div class="guided-mode-follow-up-warning__actions">
            <button type="button" class="guided-mode-follow-up-warning__button guided-mode-follow-up-warning__button--cancel" data-testid="follow-up-warning-cancel" autofocus @click="cancelFollowUpChange">{{ $t('app.dialog.button.cancel') }}</button>
            <button type="button" class="guided-mode-follow-up-warning__button guided-mode-follow-up-warning__button--continue" data-testid="follow-up-warning-continue" @click="continueFollowUpChange">{{ $t('app.dialog.button.continue') }}</button>
          </div>
        </section>
      </div>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import GuidedModeQuestion from './components/GuidedModeQuestion.vue'
import GuidedModeQuestionGroup from './components/GuidedModeQuestionGroup.vue'
import GuidedModeSequentialGroup from './components/GuidedModeSequentialGroup.vue'
import GuidedModeDemographics from './components/GuidedModeDemographics.vue'
import GuidedModeSymptoms from './components/GuidedModeSymptoms.vue'
import GuidedModeBloodPressure from './components/GuidedModeBloodPressure.vue'
import GuidedModeDate from './components/GuidedModeDate.vue'
import GuidedModeSummary from './components/GuidedModeSummary.vue'
import GuidedModeCompletionPrompt from './components/GuidedModeCompletionPrompt.vue'
import GuidedModeNavigation from './components/GuidedModeNavigation.vue'
import UnavailableAssessmentDialog from './components/UnavailableAssessmentDialog.vue'
import NetworkErrorState from '@/features/text-mode/components/NetworkErrorState.vue'
import DangerSignAlertDialog from '@/features/danger-signs/components/DangerSignAlertDialog.vue'
import DangerSignBar from '@/features/danger-signs/components/DangerSignBar.vue'
import { useDangerSignStore } from '@/features/danger-signs/stores/dangerSign.store'
import { useGuidedModeWorkflow } from './composables/useGuidedModeWorkflow'
import { useGuidedModeStore } from './stores/guidedMode'
import { clearActiveTextModeContext } from '@/features/text-mode/analytics/text-mode-context'
import { shouldResetGuidedSessionOnLeave } from '@/features/assessment/config/assessment-mode-exit-routes'
import './styles/guided-mode.css'
import '@/features/text-mode/styles/text-mode.css'
import {mdiClipboardCheckOutline} from "@mdi/js";
import AssessmentModePlaceholder from "@/components/AssessmentMode/AssessmentModePlaceholder.vue";

export default defineComponent({
  name: 'GuidedModePage',
  data: () => ({ mdiClipboardCheckOutline }),
  components: {
    UnavailableAssessmentDialog,
    NetworkErrorState,
    DangerSignAlertDialog,
    DangerSignBar,
    AssessmentModePlaceholder,
    GuidedModeBloodPressure,
    GuidedModeDate,
    GuidedModeDemographics,
    GuidedModeQuestion,
    GuidedModeQuestionGroup,
    GuidedModeSequentialGroup,
    GuidedModeSummary,
    GuidedModeCompletionPrompt,
    GuidedModeSymptoms,
    GuidedModeNavigation
  },
  setup() {
    const handlers = useGuidedModeWorkflow()

    function handleSequentialGroupAnswer(payload: { conceptId: string; value: unknown }) {
      handlers.setSequentialGroupAnswer(payload.conceptId, payload.value as string | number | string[] | null)
    }

    function handleSequentialGroupOptionSelected(payload: { conceptId: string; value: string }) {
      handlers.selectSequentialGroupOption(payload.conceptId, payload.value)
    }

    function handleSequentialGroupBloodPressurePart(payload: {
      conceptId: string
      part: 'systolic' | 'diastolic'
      value: string
    }) {
      handlers.setSequentialGroupBloodPressurePart(payload.conceptId, payload.part, payload.value)
    }

    function handleSequentialGroupUnknownAnswer(payload: { conceptId: string }) {
      handlers.setSequentialGroupUnknownAnswer(payload.conceptId)
    }

    function handleSequentialGroupDate(payload: { conceptId: string; value: string }) {
      handlers.setSequentialGroupDate(payload.conceptId, payload.value)
    }

    function handleSequentialGroupToggleSize(payload: { conceptId: string }) {
      handlers.toggleMediaSize(payload.conceptId)
    }

    function handleSequentialGroupEntryComplete(payload: { conceptId: string }) {
      handlers.markMediaEntryComplete(payload.conceptId)
    }

    function handleSequentialGroupToggleAudio(payload: { conceptId: string }) {
      handlers.toggleMediaAudio(payload.conceptId)
    }

    return {
      ...handlers,
      dangerSignStore: useDangerSignStore(),
      handleSequentialGroupAnswer,
      handleSequentialGroupOptionSelected,
      handleSequentialGroupBloodPressurePart,
      handleSequentialGroupUnknownAnswer,
      handleSequentialGroupDate,
      handleSequentialGroupToggleSize,
      handleSequentialGroupEntryComplete,
      handleSequentialGroupToggleAudio,
    }
  },
  beforeRouteLeave(to, _from, next) {
    if (to.path !== '/overall-assessment') clearActiveTextModeContext()
    if (shouldResetGuidedSessionOnLeave(to)) {
      useGuidedModeStore().endSession()
      useDangerSignStore().reset()
    }
    next()
  },
})
</script>
